import { createApi } from '@reduxjs/toolkit/query/react'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const invoicesApi = createApi({
  reducerPath: 'invoicesApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['Invoices'],
  endpoints: (builder) => ({
    getInvoices: builder.query({
      query: (requestParams: GetInvoicesParam) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getInvoices',
          requestParams
        ),
      providesTags: ['Invoices']
    }),
    getInvoiceDetail: builder.query({
      query: (requestParams: InvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getInvoiceGeneralDetailByID',
          requestParams
        )
    }),
    getFareCappingReport: builder.query({
      query: (requestParams: InvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getFareCappingReportByID',
          requestParams
        )
    }),
    getIncludedTargetGroupOffers: builder.mutation({
      query: (requestParams: { TargetGroupID: string }) =>
        generateRPCQuery(
          apiConstants.TARGET_GROUP_RPC_PATH,
          'getIncludedTargetGroupOffers',
          requestParams
        )
    }),
    createFareCappingInvoiceReport: builder.mutation({
      query: (requestParams: CreateFareCappingParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'createFareCappingInvoiceReport',
          requestParams
        ),
      invalidatesTags: ['Invoices']
    }),
    getInvoiceSummaryByID: builder.query({
      query: (requestParams: InvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getInvoiceSummaryByID',
          requestParams
        )
    }),
    getEarningsInInvoiceTimeRange: builder.query({
      query: (requestParams: CommonInvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getEarningsInInvoiceTimeRange',
          requestParams
        )
    }),
    getPointAgingInInvoiceTimeRange: builder.query({
      query: (requestParams: PointAgingInvoiceParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getPointAgingInInvoiceTimeRange',
          requestParams
        )
    }),
    getRedemptionsInInvoiceTimeRange: builder.query({
      query: (requestParams: CommonInvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getRedemptionsInInvoiceTimeRange',
          requestParams
        )
    }),
    getRedemptionsPointsUsage: builder.query({
      query: (requestParams: CommonInvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getRedemptionsPointsUsage',
          requestParams
        )
    }),
    getCouponsInInvoiceTimeRange: builder.query({
      query: (requestParams: CommonInvoiceDetailParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'getCouponsInInvoiceTimeRange',
          requestParams
        )
    }),
    createPointsIssuanceInvoiceReport: builder.mutation({
      query: (requestParams: CreatePointsIssuanceParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'createInvoiceReport',
          requestParams
        ),
      invalidatesTags: ['Invoices']
    }),
    validateInvoice: builder.mutation({
      query: (requestParams: PointsIssuanceValidationParams) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'validateInvoice',
          requestParams
        )
    }),
    stopInvoiceGeneration: builder.mutation({
      query: (requestParams: { InvoiceID: string }) =>
        generateRPCQuery(
          apiConstants.INVOICES_RPC_PATH,
          'cancelGenerateInvoice',
          requestParams
        ),
      invalidatesTags: ['Invoices']
    })
  })
})

export const {
  useGetInvoicesQuery,
  useGetInvoiceDetailQuery,
  useGetFareCappingReportQuery,
  useGetIncludedTargetGroupOffersMutation,
  useCreateFareCappingInvoiceReportMutation,
  useGetInvoiceSummaryByIDQuery,
  useGetEarningsInInvoiceTimeRangeQuery,
  useGetPointAgingInInvoiceTimeRangeQuery,
  useGetRedemptionsPointsUsageQuery,
  useGetRedemptionsInInvoiceTimeRangeQuery,
  useGetCouponsInInvoiceTimeRangeQuery,
  useCreatePointsIssuanceInvoiceReportMutation,
  useValidateInvoiceMutation,
  useStopInvoiceGenerationMutation
} = invoicesApi
